import { useEffect, useMemo, useState } from "react";
import { GeoJSON, useMap } from "react-leaflet";
import L, { type PathOptions } from "leaflet";
import type { Feature, FeatureCollection } from "geojson";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import FloraIQMap from "@/components/shared/FloraIQMap";
import { useAreasPiloto, useSatelliteVegetationQuadras, useVegetationSeries } from "@/hooks";
import type { QuadraVegetationApi, QuadrasVegetationApi } from "@/lib/api";

// Classes de NDVI para area urbana densa: as quadras do Setor Bela Vista ficam
// entre ~0,04 e ~0,30. Classes fixas (e nao por quantil) para o mapa de uma
// data ser comparavel ao de outra.
const CLASSES: Array<{ max: number; color: string; label: string }> = [
  { max: 0.1, color: "#B3412F", label: "< 0,10" },
  { max: 0.15, color: "#E07B39", label: "0,10 – 0,15" },
  { max: 0.2, color: "#E8C25A", label: "0,15 – 0,20" },
  { max: 0.25, color: "#8DBB5E", label: "0,20 – 0,25" },
  { max: Infinity, color: "#1B6E4F", label: "≥ 0,25" },
];
const NO_DATA = "#9AA3A8";
const PREFERRED_AREA = "SBV-REAL-001";

const colorFor = (ndvi: number) => (CLASSES.find((c) => ndvi < c.max) ?? CLASSES[CLASSES.length - 1]).color;
const fmtNdvi = (v: number | null | undefined) => (v == null ? "—" : v.toFixed(3).replace(".", ","));
const fmtPct = (v: number | null | undefined) => (v == null ? "—" : `${(v * 100).toFixed(1).replace(".", ",")}%`);
const fmtDate = (iso: string | null | undefined) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR") : "—");
// Os poligonos AREA-* sao areas especiais da base cadastral (escola, praca), nao quadras.
const placeLabel = (code: string) => (code.startsWith("AREA-") ? `Área especial ${code.slice(5)}` : `Quadra ${code}`);
const shortDate = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
};

function FitToQuadras({ collection }: { collection: FeatureCollection }) {
  const map = useMap();
  useEffect(() => {
    const bounds = L.geoJSON(collection).getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20] });
  }, [collection, map]);
  return null;
}

function QuadrasMap({
  data,
  selectedId,
  onSelect,
}: {
  data: QuadrasVegetationApi;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const collection = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: data.quadras.map((q) => ({
        type: "Feature",
        id: q.quadra_id,
        properties: q,
        geometry: q.geometry as Feature["geometry"],
      })),
    }),
    [data],
  );

  const style = (feature?: Feature): PathOptions => {
    const q = feature?.properties as QuadraVegetationApi | undefined;
    const selected = q?.quadra_id === selectedId;
    const measured = q?.ndvi_mean != null;
    return {
      fillColor: measured ? colorFor(q!.ndvi_mean as number) : NO_DATA,
      fillOpacity: measured ? 0.75 : 0.15,
      color: selected ? "#0B3D2A" : "#FFFFFF",
      weight: selected ? 3 : 1,
    };
  };

  return (
    <FloraIQMap className="h-full w-full" defaultBaseLayer="hybrid" minZoom={13}>
      <GeoJSON
        key={`${data.quadras.length}|${selectedId ?? ""}`}
        data={collection}
        style={style}
        onEachFeature={(feature, layer) => {
          const q = feature.properties as QuadraVegetationApi;
          layer.bindTooltip(
            `<strong>${placeLabel(q.code)}</strong><br/>${
              q.ndvi_mean != null ? `NDVI ${fmtNdvi(q.ndvi_mean)} em ${fmtDate(q.scene_date)}` : "Sem observação válida"
            }`,
            { sticky: true },
          );
          layer.on("click", () => onSelect(q.quadra_id));
        }}
      />
      <FitToQuadras collection={collection} />
    </FloraIQMap>
  );
}

function SeriesChart({ quadraId, areaId, quadraCode }: { quadraId: number | null; areaId: number; quadraCode: string | null }) {
  const { data: quadra } = useVegetationSeries("quadra", quadraId);
  const { data: area, loading } = useVegetationSeries("area", areaId);

  const rows = useMemo(() => {
    const byDate = new Map<string, { date: string; Quadra?: number; Área?: number }>();
    for (const o of area?.observations ?? []) {
      if (o.ndvi_mean != null) byDate.set(o.scene_date, { date: o.scene_date, Área: o.ndvi_mean });
    }
    for (const o of quadra?.observations ?? []) {
      if (o.ndvi_mean == null) continue;
      byDate.set(o.scene_date, { ...(byDate.get(o.scene_date) ?? { date: o.scene_date }), Quadra: o.ndvi_mean });
    }
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [quadra, area]);

  if (loading && rows.length === 0) return <p className="text-xs text-foreground-500 py-8 text-center">Carregando série...</p>;
  if (rows.length === 0) return <p className="text-xs text-foreground-500 py-8 text-center">Sem observações no período.</p>;

  return (
    <div className="h-[220px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#6B737A" }} minTickGap={28} />
          <YAxis domain={[0, "auto"]} tickFormatter={(v: number) => v.toFixed(2)} tick={{ fontSize: 10, fill: "#6B737A" }} width={42} />
          <Tooltip
            labelFormatter={(v) => fmtDate(String(v))}
            formatter={(v) => fmtNdvi(Number(v))}
            contentStyle={{ borderRadius: 8, border: "1px solid #E8EAED", fontSize: 12 }}
          />
          <Legend iconType="plainline" formatter={(v: string) => (
            <span className="text-xs text-foreground-600">{v === "Quadra" && quadraCode ? placeLabel(quadraCode) : "Média da área"}</span>
          )} />
          <Line type="monotone" dataKey="Área" stroke="#9AA3A8" strokeWidth={1.5} dot={false} connectNulls />
          {quadraId != null && (
            <Line type="monotone" dataKey="Quadra" stroke="#1B6E4F" strokeWidth={2} dot={{ r: 2 }} connectNulls />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function SatelliteVegetationSection() {
  const { areasPiloto, loading: loadingAreas } = useAreasPiloto();
  const area = useMemo(
    () => areasPiloto.find((a) => a.code === PREFERRED_AREA) ?? null,
    [areasPiloto],
  );
  const areaId = area ? Number(area.id) : null;
  const { data, loading, error } = useSatelliteVegetationQuadras(areaId);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const selected = data?.quadras.find((q) => q.quadra_id === selectedId) ?? null;
  const measured = data?.quadras.filter((q) => q.ndvi_mean != null) ?? [];
  const lastScene = measured.reduce<string | null>((acc, q) => (q.scene_date && (!acc || q.scene_date > acc) ? q.scene_date : acc), null);
  const ranking = [...measured].sort((a, b) => (b.ndvi_mean ?? 0) - (a.ndvi_mean ?? 0));

  return (
    <section className="bg-white rounded-card border border-background-200 p-5 md:p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground-900 font-heading flex items-center gap-2">
            <i className="ri-earth-line text-accent-500" aria-hidden="true"></i>
            Vegetação por satélite (Sentinel-2)
          </h2>
          <p className="text-xs text-foreground-500 mt-0.5 max-w-3xl">
            NDVI é um índice de presença e vigor da vegetação (de −1 a 1), medido do mesmo jeito em todas as quadras e em
            todas as datas. Serve para comparar quadras e acompanhar a evolução — não é percentual de copa: com pixel de
            10 m, árvores pequenas se misturam a telhado e asfalto.
          </p>
        </div>
        {data && (
          <div className="text-xs text-foreground-600 text-right">
            <p>
              Última passagem: <strong>{fmtDate(lastScene)}</strong> · {measured.length} de {data.quadras.length} quadras
            </p>
            {data.last_run && (
              <p className="text-foreground-500">
                Atualizado em {new Date(data.last_run.finished_at ?? data.last_run.started_at).toLocaleString("pt-BR")}
                {data.last_run.status !== "SUCCEEDED" && ` · execução ${data.last_run.status.toLowerCase()}`}
              </p>
            )}
          </div>
        )}
      </div>

      {(loadingAreas || loading) && <p className="text-sm text-foreground-500 py-6">Carregando...</p>}
      {!loadingAreas && !area && (
        <p className="text-sm text-foreground-500 py-6">Área piloto {PREFERRED_AREA} não encontrada.</p>
      )}
      {error && <p className="text-sm text-red-700 py-6">Não foi possível carregar a vegetação por satélite: {error}</p>}

      {data && measured.length === 0 && (
        <p className="text-sm text-foreground-500 py-6">
          Ainda não há observações de satélite para esta área. Elas são gravadas pela rotina semanal de atualização.
        </p>
      )}

      {data && measured.length > 0 && areaId != null && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3">
            <div className="h-[420px] rounded-lg overflow-hidden border border-background-200">
              <QuadrasMap data={data} selectedId={selectedId} onSelect={setSelectedId} />
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-foreground-600">
              <span className="font-medium">NDVI médio da quadra</span>
              {CLASSES.map((c) => (
                <span key={c.label} className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: c.color }} />
                  {c.label}
                </span>
              ))}
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm opacity-40" style={{ background: NO_DATA }} />
                sem observação
              </span>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-3">
            {selected ? (
              <div className="rounded-lg border border-background-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground-900">{placeLabel(selected.code)}</p>
                  <button type="button" onClick={() => setSelectedId(null)} className="text-xs text-foreground-500 hover:underline">
                    limpar
                  </button>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2 text-xs">
                  <dt className="text-foreground-500">NDVI médio</dt>
                  <dd className="font-medium text-foreground-900">{fmtNdvi(selected.ndvi_mean)}</dd>
                  <dt className="text-foreground-500">Área com NDVI ≥ 0,30</dt>
                  <dd className="font-medium text-foreground-900">{fmtPct(selected.veg_fraction_ndvi_030)}</dd>
                  <dt className="text-foreground-500">Área com NDVI ≥ 0,50</dt>
                  <dd className="font-medium text-foreground-900">{fmtPct(selected.veg_fraction_ndvi_050)}</dd>
                  <dt className="text-foreground-500">Data da passagem</dt>
                  <dd className="font-medium text-foreground-900">{fmtDate(selected.scene_date)}</dd>
                  <dt className="text-foreground-500">Copa estimada (FITA)</dt>
                  <dd className="font-medium text-foreground-900">
                    {selected.fita_canopy_percent != null ? `${selected.fita_canopy_percent}%` : "sem inspeção"}
                  </dd>
                  <dt className="text-foreground-500">Observações válidas</dt>
                  <dd className="font-medium text-foreground-900">{selected.observation_count}</dd>
                </dl>
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 p-3">
                <p className="text-xs font-medium text-foreground-700 mb-2">Quadras mais e menos verdes (última passagem)</p>
                <ol className="text-xs space-y-1">
                  {[...ranking.slice(0, 4), ...(ranking.length > 8 ? ranking.slice(-4) : [])].map((q, i, arr) => (
                    <li key={q.quadra_id} className={i === 4 && arr.length === 8 ? "pt-1 border-t border-background-200" : ""}>
                      <button type="button" onClick={() => setSelectedId(q.quadra_id)} className="flex w-full justify-between hover:underline">
                        <span>{placeLabel(q.code)}</span>
                        <span className="font-mono">{fmtNdvi(q.ndvi_mean)}</span>
                      </button>
                    </li>
                  ))}
                </ol>
                <p className="text-[11px] text-foreground-500 mt-2">Clique numa quadra no mapa para ver a série dela.</p>
              </div>
            )}

            <div className="rounded-lg border border-background-200 p-3">
              <p className="text-xs font-medium text-foreground-700 mb-1">Evolução do NDVI</p>
              <SeriesChart quadraId={selectedId} areaId={areaId} quadraCode={selected?.code ?? null} />
              <p className="text-[11px] text-foreground-500 mt-1">
                Só entram passagens com pelo menos 80% da quadra sem nuvem. A queda entre junho e outubro é a estação
                seca, não perda de árvores.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
